import { type LocationQuery } from 'vue-router';
import TripModule from '../TripModule';
import { FlightFilters, type FlightFiltersData } from './FlightModuleTypes';
import FlightAutoPriceClass from './FlightAutoPriceClass';
import { type FlightSearchForm } from '@search/trip/flight/FlightSearchForm';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Flight from '@itinerary/trip/flight/Flight';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';
import FlightPackageSet from '@itinerary/trip/flight/FlightPackageSet';
import type FlightPriceClass from '@itinerary/trip/flight/FlightPriceClass';
import { type FlightSegment } from '@itinerary/trip/flight/FlightSegment';
import type TripSegment from '@itinerary/trip/TripSegment';
import {
    DurationFilter,
    SortDirection,
    TimeFilter,
    TripSortBy,
} from '@itinerary/trip/TripModuleTypes';
import {
    CheckboxFilter,
    type Filter,
    type IdTitle,
    InputFilter,
    LoadingStatus,
    PriceFilter,
    RadioFilter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import { AutoReserveType } from '@utils/itinerary';
import { type Location } from '@utils/types';
import { type FlightPathData, type FlightUrlParams } from '@utils/flight';
import { TripType } from '@utils/trip';
import { type FlightPackageLoaderConfig } from '@itinerary/trip/flight/types';

export default class FlightModule extends TripModule<
    Flight,
    FlightPriceClass,
    FlightFiltersData,
    Location,
    FlightPackage,
    FlightPackageSet
> {
    override get defaultAutoReserveType() {
        return AutoReserveType.Item;
    }

    override get searchResultAttributes() {
        return {
            ...super.searchResultAttributes,
            isHigherCapacityLoaded:
                this.currentPackageSet?.isHigherCapacityLoaded,
            hasHigherCapacityCard: this.sortedReservableList.some(
                pkg => pkg.isHigherCapacity
            ),
            hasHigherCapacityPotential:
                this.currentPackageSet?.filterMetaData
                    ?.HasHigherPriceCapacityCard,
        };
    }

    override getPackageSet(
        loaderConfig: FlightPackageLoaderConfig
    ): FlightPackageSet {
        return new FlightPackageSet(loaderConfig, this.getInitialFilters());
    }

    override get reserveInfo() {
        return this.flightForm.reserveInfo;
    }

    override get metaTagTitle(): string {
        return `خرید بلیط هواپیما ${this.originName} ${this.destinationName} با بهترین قیمت - مِستربلیط`;
    }

    override descriptionTemplate =
        'خرید اینترنتی بلیط هواپیما (مبدا) (مقصد) همراه با بلیط های لحظه آخری و برنامه پروازها. مسیر و تاریخ را مشخص کنید و سپس بلیط هواپیما (مبدا) (مقصد) را به صورت آنلاین رزرو کنید. مِستربلیط سایت و اپلیکیشن رزرو بلیط هواپیما با ارزان‌ترین قیمت و بدون نیاز به ثبت نام';

    override get emptyPackage() {
        const flightForm = useFlightForm();
        let departureTime =
            flightForm?.departureDate?.toISOString() ||
            new Date().toISOString();
        try {
            departureTime = dayConfig
                .gregory(flightForm?.departureDate)
                .endOf('day')
                .toISOString();
        } catch {}

        const flightPackage = new FlightPackage(
            {
                Segments: [
                    {
                        ConnectionTime: '00:00:00',
                        TotalTime: '00:00:00',
                        Legs: [
                            {
                                Destination: flightForm.destination?.title,
                                Origin: flightForm.origin?.title,
                                DepartureTime: departureTime,
                                DepartureDateString: departureTime,
                            },
                        ],
                    },
                ],
                AutomaticReserve: true,
                Prices: [
                    {
                        Capacity: 0,
                    },
                ],
            },
            {
                adultCount: flightForm.adultCount,
                childCount: flightForm.childCount,
                infantCount: flightForm.infantCount,
            },
            {}
        );
        flightPackage.selectedPrice = new FlightAutoPriceClass(
            flightForm.isForeign
        );
        return flightPackage;
    }

    override get defaultSortBy(): TripSortBy {
        return TripSortBy.Price;
    }

    private get flightForm(): FlightSearchForm {
        return useFlightForm();
    }

    private filterTime(segmentTime: number, selectedFilters: string[]) {
        return (
            (segmentTime <= 8 * 60 && selectedFilters.includes('midnight')) ||
            (segmentTime >= 8 * 60 &&
                segmentTime <= 18 * 60 &&
                selectedFilters.includes('day')) ||
            (segmentTime >= 18 * 60 && selectedFilters.includes('night'))
        );
    }

    private filterClass(pkg: FlightPackage, selectedClasses: string[]) {
        return selectedClasses.includes(pkg.defaultPrice?.cabinClass || '');
    }

    private flightNumberFilter(pkg: FlightPackage, flightNumber: string) {
        return pkg.segments.some(seg =>
            seg.trips.some(trip =>
                trip.flightNumber.trim().startsWith(flightNumber.trim())
            )
        );
    }

    private airlineFilter(pkg: FlightPackage, selectedAirlines?: string[]) {
        let res = true;
        if (selectedAirlines?.length) {
            res &&= selectedAirlines.some(a =>
                pkg.segments.some(s =>
                    s.trips.some(f => f.corporationInfo.code === a)
                )
            );
        }
        return res;
    }

    private timeFilter(
        filters: Record<
            (typeof FlightFilters)[keyof typeof FlightFilters],
            any
        >,
        {
            departureMinutes,
            arrivalMinutes,
        }: { departureMinutes: number; arrivalMinutes: number }
    ) {
        let result = true;
        if (filters.time) {
            if (filters.time.departure?.length) {
                result &&= this.filterTime(
                    departureMinutes,
                    filters.time.departure
                );
            }
            if (filters.time.arrival?.length) {
                result &&= this.filterTime(
                    arrivalMinutes,
                    filters.time.arrival
                );
            }
        }

        return result;
    }

    private baggageFilter(baggage: string[] | undefined, pkg: FlightPackage) {
        return baggage?.length
            ? baggage.includes(pkg.defaultPrice?.baggage.toString() || '')
            : true;
    }

    override applyFilter(
        pkg: FlightPackage,
        filters: Record<(typeof FlightFilters)[keyof typeof FlightFilters], any>
    ): boolean {
        if (!pkg.segments?.length) {
            return false;
        }

        const departureMinutes = timeToMinutes(pkg.departureTime);
        const arrivalMinutes = timeToMinutes(pkg.arrivalTime);
        const stopCounts = pkg.segments.map(
            s =>
                s.trips.length +
                s.trips.reduce((acc, t) => acc + t.stops, 0) -
                1
        );

        const stops = ([] as string[]).concat(
            ...pkg.segments.map(s => s.trips.slice(1).map(t => t.originCode))
        );
        let result = true;

        result &&= this.airlineFilter(pkg, filters.airline);

        if (filters.price) {
            result &&=
                pkg.singlePayable >= filters.price[0] &&
                pkg.singlePayable <= filters.price[1];
        }

        result &&= this.timeFilter(filters, {
            departureMinutes,
            arrivalMinutes,
        });

        result &&= this.baggageFilter(filters.baggage, pkg);

        if (filters.stopCount) {
            result &&= stopCounts.every(c => c <= parseInt(filters.stopCount));
        }
        if (filters.stops?.length) {
            result &&=
                !!stops.length && stops.every(s => filters.stops.includes(s));
        }
        if (filters.classOfService?.length) {
            result &&= this.filterClass(pkg, filters.classOfService);
        }
        if (filters.duration?.departure) {
            result &&=
                Math.ceil(pkg.segments[0].duration / 3600) >=
                    filters.duration.departure[0] &&
                Math.ceil(pkg.segments[0].duration / 3600) <=
                    filters.duration.departure[1];
        }
        if (filters.duration?.return) {
            result &&=
                Math.ceil(pkg.segments[1].duration / 3600) >=
                    filters.duration.return[0] &&
                Math.ceil(pkg.segments[1].duration / 3600) <=
                    filters.duration.return[1];
        }
        if (filters.flightNumber?.trim()) {
            result &&= this.flightNumberFilter(pkg, filters.flightNumber);
        }
        if (filters.labels?.length) {
            result &&= filters.labels.some((a: string) =>
                pkg.specialOffers.some(offer => offer.Title === a)
            );
        }
        return result;
    }

    get meta() {
        return (
            (this.currentPackageSet
                ?.filterMetaData as FlightFiltersData['meta']) || null
        );
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet?.filterMetaData) {
            return [];
        }
        const { flightForm } = this;
        const meta = this.currentPackageSet.filterMetaData;

        const stopsArr = new Array<TripSegment<Flight>>()
            .concat(
                ...this.list
                    .filter(p => p.segments)
                    .map(p => p.segments as TripSegment<Flight>[])
            )
            .filter(s => s.trips?.length > 1)
            .map(s =>
                s.trips
                    .slice(1)
                    .map(t => ({ title: t.origin, id: t.originCode }))
            );
        const stops = new Array<{ title: string; id: string }>().concat(
            ...stopsArr
        );
        const uniqueStops = stops.filter(
            s => stops.find(ss => ss.id === s.id) === s
        );
        const labelsFilterList: IdTitle[] = this.currentPackageSet.list
            .filter(pkg => pkg.specialOffers)
            .map(pkg => pkg.specialOffers)
            .map(label => label.map(label => label.Title ?? ''))
            .flat()
            .filter((value, index, array) => array.indexOf(value) === index)
            .map(item => ({ id: item, title: item }));
        const filters: Filter[] = [
            new PriceFilter(
                FlightFilters.Price,
                meta.MinPrice ?? 0,
                meta.MaxPrice ?? 0
            ),
            ...(meta.CabinClasses
                ? [
                      new CheckboxFilter(
                          FlightFilters.ClassOfService,
                          meta.CabinClasses?.map(
                              ({ ClassOfService, DisplayName }) => ({
                                  id: ClassOfService || '',
                                  title: DisplayName || '-',
                              })
                          ) || [],
                          'کلاس پروازی'
                      ),
                  ]
                : []),
            ...(flightForm.isDomestic
                ? []
                : [
                      new RadioFilter(
                          FlightFilters.StopCount,
                          [...Array(4).keys()].map(i => ({
                              id: i.toString(),
                              title: i === 0 ? 'بدون توقف' : `${i} توقف و کمتر`,
                          })),
                          'تعداد توقف'
                      ),
                  ]),
            new TimeFilter(
                FlightFilters.Time,
                flightForm.origin?.title ?? '',
                flightForm.destination?.title ?? ''
            ),
            new CheckboxFilter(
                FlightFilters.Stops,
                uniqueStops,
                'فرودگاه‌های توقف'
            ),
            new CheckboxFilter(
                FlightFilters.Airline,
                meta.Airlines?.map(a => ({
                    title: a.PersianTitle ?? '',
                    id: a.IataCode ?? '',
                })) || [],
                'شرکت هواپیمایی'
            ),
            new CheckboxFilter(
                FlightFilters.Baggage,
                meta.BaggageInfos?.map(a => ({
                    title: a.Title ?? '',
                    id: a.Baggage?.toString() ?? '',
                })) || [],
                'میزان بار پرواز'
            ),
            new CheckboxFilter(
                FlightFilters.Labels,
                labelsFilterList,
                'برچسب‌ها'
            ),
        ];
        if (flightForm.isForeign) {
            const departureList = this.list
                .map(pck => pck.segments[0])
                .map(seg => seg as FlightSegment)
                .map(seg => seg.duration);
            const returnList = this.list
                .map(pck => pck.segments[1])
                .map(seg => seg as FlightSegment)
                .map(seg => seg?.duration || 0);
            const departureMin = departureList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue < previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                Number.MAX_VALUE
            );
            const departureMax = departureList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue > previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                0
            );
            const returnMin = returnList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue < previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                Number.MAX_VALUE
            );
            const returnMax = returnList.reduce(
                (previousValue, currentValue) => {
                    if (currentValue > previousValue) {
                        return currentValue;
                    }
                    return previousValue;
                },
                0
            );
            filters.push(
                new DurationFilter(
                    flightForm.tripType === TripType.RoundTrip,
                    {
                        departure: Math.ceil(departureMin / 3600),
                        return:
                            flightForm.tripType === TripType.RoundTrip
                                ? Math.ceil(returnMin / 3600)
                                : undefined,
                    },
                    {
                        departure: Math.ceil(departureMax / 3600),
                        return:
                            flightForm.tripType === TripType.RoundTrip
                                ? Math.ceil(returnMax / 3600)
                                : undefined,
                    }
                )
            );
            filters.push(
                new InputFilter(FlightFilters.FlightNumber, 'شماره پرواز', {
                    placeholder: 'شماره پرواز',
                })
            );
        }
        return this.sortFilters(filters);
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return new TaxiLateralLoader();
    }

    override getItineraryPackageLoaderConfigs(
        params: FlightUrlParams
    ): FlightPackageLoaderConfig[] {
        const { flightForm } = this;
        const domesticAirportMap = flightForm.domesticAirportCodesMap;
        const isForeignRoundTrip =
            params.routes.length === 1 &&
            params.routes[0].returnDate &&
            !(
                domesticAirportMap[params.routes[0].origin] &&
                domesticAirportMap[params.routes[0].destination]
            );

        // When it is a foreign round-trip, we must produce just one loader
        if (isForeignRoundTrip) {
            return [
                {
                    originCode: params.routes[0].origin,
                    destinationCode: params.routes[0].destination,
                    date: params.routes[0].departureDate,
                    adultCount: params.adultCount,
                    childCount: params.childCount,
                    infantCount: params.infantCount,
                    cabinClass: params.cabinClass,
                    returnDate: params.routes[0].returnDate,
                    url: params.url,
                    isForeign: flightForm.isForeign,
                    isCapacityTrackCampaign: flightForm.isCapacityTrackCampaign,
                    token: useAuthStore().token,
                    xplayerID: useAuthStore().uuid ?? undefined,
                    totalSeatCount: 0, // fake
                    isRoundTrip: this.isRoundTrip,
                },
            ];
        }

        // Otherwise, we must generate a loader for each route and each date
        return params.routes
            .map(route =>
                [
                    <FlightPackageLoaderConfig>{
                        originCode: params.routes[0].origin,
                        destinationCode: params.routes[0].destination,
                        date: params.routes[0].departureDate,
                        adultCount: params.adultCount,
                        childCount: params.childCount,
                        infantCount: params.infantCount,
                        cabinClass: params.cabinClass,
                        url: params.url,
                        isForeign: flightForm.isForeign,
                        token: useAuthStore().token,
                        xplayerID: useAuthStore().uuid ?? undefined,
                        originName: flightForm.origin?.title,
                        destinationName: flightForm.destination?.title,
                        totalSeatCount: 0, // fake
                        isRoundTrip: this.isRoundTrip,
                    },
                    route.returnDate
                        ? <FlightPackageLoaderConfig>{
                              originCode: route.destination,
                              destinationCode: route.origin,
                              date: route.returnDate,
                              adultCount: params.adultCount,
                              childCount: params.childCount,
                              infantCount: params.infantCount,
                              cabinClass: params.cabinClass,
                              url: params.url,
                              isForeign: flightForm.isForeign,
                              token: useAuthStore().token,
                              xplayerID: useAuthStore().uuid ?? undefined,
                              originName: flightForm.destination?.title,
                              destinationName: flightForm.origin?.title,
                              totalSeatCount: 0, // fake
                              isRoundTrip: this.isRoundTrip,
                          }
                        : null,
                ]
                    // Eliminate null values
                    .filter<FlightPackageLoaderConfig>(
                        Boolean as unknown as (
                            value: FlightPackageLoaderConfig | null
                        ) => value is FlightPackageLoaderConfig
                    )
            )
            .flat();
    }

    override getInitialFilters(): { [key in FlightFiltersData['key']]: any } {
        return {
            airline: null,
            duration: null,
            price: null,
            stopCount: null,
            stops: null,
            time: null,
            classOfService: [],
            labels: null,
            flightNumber: '',
            baggage: [],
        };
    }

    override get dateTitle(): string {
        const { isDomestic, tripType, returnDate } = this.flightForm;
        if (!isDomestic && tripType === TripType.RoundTrip && returnDate) {
            return `${super.dateTitle} به ${dayConfig
                .js(returnDate)
                .format(formatter({ format: 'long', includeWeekday: true }))}`;
        } else {
            return super.dateTitle;
        }
    }

    override get sortByList(): SortBy[] {
        const sortList = [
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: `${TripSortBy.Time}-${SortDirection.Desc}`,
                label: 'دیرترین زمان حرکت',
            },
            {
                type: `${TripSortBy.Price}-${SortDirection.Desc}`,
                label: 'گران‌ترین قیمت',
            },
        ];
        if (this.flightForm.isForeign) {
            sortList.push({
                type: TripSortBy.Duration,
                label: 'سریع‌ترین زمان',
            });
        }
        return sortList;
    }

    override async formAutoReserve(query: LocationQuery) {
        const { flightForm } = this;
        const params = await flightForm.parsePath(query as FlightPathData);
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        await flightForm.loadViewUrlParams(params);
        searchFormStore.urlParams = params;
        itineraryStore.initItineraryModule(params);
        searchFormStore.initSearchForm();
        if (this.currentLoadingStatus !== LoadingStatus.Loading) {
            itineraryStore.setPackageSetIndex(0);
            if (!this.isSetLoaded(0)) {
                await this.loadAvailable({
                    index: 0,
                    inForm: true,
                });
                if (
                    this.itineraryPackageSets[0]?.loadingStatus ===
                    LoadingStatus.Error
                ) {
                    throw new Error('در حال حاضر سرور هواپیما قطع می‌باشد.');
                }
            }
        }
        const formStore = useFormStore();
        formStore.setCounts({
            adultCount: params.adultCount,
            childCount: params.childCount,
            infantCount: params.infantCount,
        });
        const packageSelectStore = usePackageSelectStore();
        // problem here
        const returnFlight = query?.returnFlight;
        const departureFlight = query?.departureFlight;
        const departurePackage = this.sortedReservableList.find(
            ({ uniqueId }) => uniqueId === departureFlight
        );
        // leads here
        if (departurePackage) {
            this.beforeSelectPackage({
                index: 0,
                package: departurePackage,
            });
            packageSelectStore.setSelectedPackage({
                index: 0,
                package: departurePackage,
            });
        } else {
            throw new Error('پرواز یافت نشد!');
        }
        if (
            params.returnDate &&
            returnFlight &&
            this.itineraryPackageSets.length > 1
        ) {
            itineraryStore.setPackageSetIndex(1);
            if (!this.isSetLoaded(1)) {
                await this.loadAvailable({
                    index: 1,
                    inForm: true,
                });
            }
            if (
                this.itineraryPackageSets[1]?.loadingStatus ===
                LoadingStatus.Error
            ) {
                throw new Error('در حال حاضر سرور هواپیما قطع می‌باشد.');
            }
            const returnPackage = this.sortedReservableList.find(
                ({ uniqueId }) => uniqueId === returnFlight
            );
            if (returnPackage) {
                this.beforeSelectPackage({
                    index: 1,
                    package: returnPackage,
                });
                packageSelectStore.setSelectedPackage({
                    index: 1,
                    package: returnPackage,
                });
            } else {
                throw new Error('پرواز برگشت یافت نشد!');
            }
        }
        formStore.clearState();
        formStore.setItineraryPackagesAndDepartureTime(
            packageSelectStore.selectedPackages
        );
    }
}
